/**
 * Wire types for the QUESTION post facet's write path (`/ajax/question/{discussionPublicId}/*`).
 *
 * Hand-written rather than generated, per AGENTS.md rule 6: these endpoints are new and
 * `make sync-client-local` needs a running local backend plus a devo tunnel. Consumption still goes
 * through `ClientFactory`, so the rule holds. Regenerate the typed client later if you want the
 * generated methods; these shapes mirror `QuestionFacetService.QuestionStateView` and
 * `DiscussionWithContentDTO`'s three question fields.
 *
 * Design: designs/01-now/2026-07-29-post-facets-question-facet-design.md §7.3 and §8.
 */

/**
 * The three states a question can be in.
 *
 * <p>Derived SERVER-SIDE and exactly once, which is the whole point — do not reconstruct it on the
 * client from an accepted id. In particular `ANSWERED` means the accepted answer is **visible**: an
 * accepted answer that was soft-deleted or hidden by moderation sends the question back to `OPEN`
 * (Q-D10), and only the server knows that.
 */
export type QuestionState = "OPEN" | "ANSWERED" | "RESOLVED";

/**
 * What every question write verb returns, and what the discussion payload carries.
 *
 * `acceptedThoughtPublicId` is non-null **only** when `state === "ANSWERED"`. It is never the raw
 * `post_question.accepted_thought_id` pointer, which stays populated after the answer it points at
 * is gone — so a client that treats "has an id" as "is answered" will render a badge linking to an
 * answer nobody can see.
 */
// FA-9 / FI-060 (2026-09-19): the backend DTO now declares every field required (schema-only), so the generated
// model matches this shape exactly and is re-exported rather than restated.
// `state` is the generated `QuestionStateViewStateEnum`, the same three literals as `QuestionState`.
export type { QuestionStateView } from "@/generated-client";

/** Body of the accept verb. The answer is an argument to the write, not a second resource. */
// FA-9: the generated model is field-for-field identical, so it is re-exported rather than restated.
export type { AcceptAnswerRequest } from "@/generated-client";

/**
 * The two per-answer fields the thought DTOs carry on a question, read through a narrow cast the same
 * way `ThoughtPinFields` is (rule 6 — the backend gained them without a client regeneration).
 *
 * `accepted` is the badge, and it is already resolved through the answer's VISIBILITY server-side, so
 * never re-derive it by comparing this thought's id against the discussion's `acceptedThoughtPublicId`:
 * that pointer outlives a soft delete (Q-D10) and the comparison would badge an answer nobody can see.
 *
 * `canAccept` is the ASKER's permission, not this answer's property — it is false for every other
 * reader, which is exactly what makes it safe to put the control on the card rather than in a menu.
 */
export type ThoughtAcceptFields = {
    accepted?: boolean | null;
    canAccept?: boolean | null;
};

/**
 * The question fields on the discussion payload, arriving from the mount container's data attributes on
 * a direct load and from `/ajax/discussion/page-data/*` on an in-app soft navigation.
 */
export type DiscussionQuestionFields = {
    isQuestion?: boolean;
    questionState?: QuestionState;
    acceptedThoughtPublicId?: string;
};

/** Narrows the raw `data-question-state` / `/page-data` string to a `QuestionState`, or drops it. */
export function parseQuestionState(value: unknown): QuestionState | undefined {
    return value === "OPEN" || value === "ANSWERED" || value === "RESOLVED" ? value : undefined;
}
